<template>
  <b-card class="export-section mb-4 shadow-sm">
    <b-card-header class="export-header">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <feather-icon
            icon="DownloadIcon"
            size="20"
            class="mr-2 text-primary"
          />
          <h6 class="mb-0 font-weight-semibold">
            Export des données des jeunes
          </h6>
        </div>
        <b-badge
          variant="light-primary"
          class="export-badge ml-1"
        >
          Excel
        </b-badge>
      </div>
    </b-card-header>

    <b-card-body class="export-body">
      <b-row class="align-items-end">
        <!-- Filtre Type de demande -->
        <b-col lg="4" md="6" sm="12" class="align-self-start mb-3 mb-lg-0">
          <b-form-group
            label="Type de demande"
            label-for="demand-type-select"
            label-class="mt-2"
            class="mb-0"
          >
            <b-form-select
              id="demand-type-select"
              v-model="selectedDemandType"
              :options="demandTypeOptions"
              class="custom-select"
            />
          </b-form-group>
        </b-col>

        <!-- Informations période -->
        <b-col lg="4" md="6" sm="12" class="align-self-start mb-3 mb-lg-0">
          <b-form-group
            label="Période sélectionnée"
            label-for="young-statistics-start-date"
            label-class="mt-2"
            class="period-info mb-0"
          >
            <div class="period-inputs">
              <b-form-input
                id="young-statistics-start-date"
                v-model="startDate"
                type="date"
                class="period-date-input"
                :max="endDate || null"
                aria-label="Date de début"
                :aria-invalid="isDateRangeInvalid ? 'true' : 'false'"
                :aria-describedby="isDateRangeInvalid ? 'statistics-date-range-error' : null"
              />
              <span class="mx-1 flex-shrink-0">-</span>
              <b-form-input
                id="young-statistics-end-date"
                v-model="endDate"
                type="date"
                class="period-date-input"
                :min="startDate || null"
                aria-label="Date de fin"
                :aria-invalid="isDateRangeInvalid ? 'true' : 'false'"
                :aria-describedby="isDateRangeInvalid ? 'statistics-date-range-error' : null"
              />
            </div>
            <small class="form-text text-muted mt-1">
              Les statistiques se mettent à jour automatiquement. Le type de demande filtre les données liées aux jeunes; l'export Excel reprend ces filtres.
            </small>
          </b-form-group>
        </b-col>

        <!-- Bouton d'export -->
        <b-col lg="4" md="12" sm="12" class="align-self-start text-lg-right">
          <export-button
            :demand-type="selectedDemandType"
            class="export-btn-wrapper mt-lg-4"
          />
        </b-col>
      </b-row>
      <b-row v-if="isDateRangeInvalid" class="mt-1">
        <b-col lg="4" md="6" sm="12" offset-lg="4" offset-md="6">
          <small
            id="statistics-date-range-error"
            class="d-block text-danger"
            role="alert"
          >
            La date de début doit être antérieure ou égale à la date de fin.
          </small>
        </b-col>
      </b-row>

      <!-- Description des données -->
      <b-row class="mt-3">
        <b-col cols="12">
          <div class="export-description">
            <feather-icon
              icon="InfoIcon"
              size="14"
              class="mr-1"
            />
            <small class="text-muted">
              <strong>Données incluses :</strong> heure d'appel, durée, numéro unique, âge, genre, commune, région,
              département, type de demande, motif, situation d'hébergement et notoriété.
            </small>
          </div>
        </b-col>
      </b-row>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BBadge,
} from 'bootstrap-vue'
import { ref, computed, watch } from '@vue/composition-api'
import * as dayjs from 'dayjs'
import store from '@/store'
import ExportButton from './ExportButton.vue'

export default {
  name: 'ExportFilters',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BBadge,
    ExportButton,
  },
  setup() {
    const selectedDemandType = computed({
      get: () => store.getters['chart/getDemandType'],
      set: value => store.dispatch('chart/setDemandType', value),
    })
    const startDate = ref('')
    const endDate = ref('')

    const demandTypeOptions = [
      { value: 0, text: 'Tous les types' },
      { value: 1, text: 'LGBTQIA+' },
      { value: 2, text: 'Autre type' },
      { value: 3, text: 'Menaces/Canulars/Insultes' },
    ]

    const period = computed(() => store.getters['chart/getPeriod'])

    const isDateRangeInvalid = computed(() => Boolean(
      startDate.value
      && endDate.value
      && startDate.value > endDate.value,
    ))

    const formatDateInput = value => {
      if (!value) {
        return ''
      }

      const date = dayjs(value)
      return date.isValid() ? date.format('YYYY-MM-DD') : ''
    }

    watch(period, selectedPeriod => {
      startDate.value = formatDateInput(selectedPeriod.start)
      endDate.value = formatDateInput(selectedPeriod.end)
    }, { immediate: true })

    watch([startDate, endDate], ([start, end]) => {
      if (!start || !end || start > end) {
        return
      }

      const startOfDay = dayjs(start).startOf('day')
      const endOfDay = dayjs(end).endOf('day')
      const currentStart = period.value.start ? dayjs(period.value.start).valueOf() : null
      const currentEnd = period.value.end ? dayjs(period.value.end).valueOf() : null

      if (currentStart === startOfDay.valueOf() && currentEnd === endOfDay.valueOf()) {
        return
      }

      store.dispatch('chart/setStartDate', startOfDay)
      store.dispatch('chart/setEndDate', endOfDay)
    })

    return {
      selectedDemandType,
      demandTypeOptions,
      startDate,
      endDate,
      isDateRangeInvalid,
    }
  },
}
</script>

<style lang="scss" scoped>
  @import '~@core/scss/base/bootstrap-extended/include';
  @import '~@core/scss/base/components/variables-dark';

.export-section {
  border: 1px solid $border-color;
  border-radius: 8px;
  transition: all 0.2s ease;
  background-color: $card-bg;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
  }
}

.export-header {
  background: linear-gradient(135deg, $body-bg 0%, darken($body-bg, 3%) 100%);
  border-bottom: 1px solid $border-color;
  padding: 1rem 1.25rem;
  border-radius: 8px 8px 0 0;
}

.export-badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  border-radius: 12px;
}

.export-body {
  padding: 1.5rem 1.25rem;
  background-color: $card-bg;
}

.custom-select {
  border-radius: 6px;
  border: 1px solid $input-border-color;
  transition: all 0.2s ease;
  background-color: $input-bg;
  color: $body-color;

  &:focus {
    border-color: $primary;
    background-color: $input-bg;
    color: $body-color;
  }
}

.period-info {
  .period-inputs {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .period-date-input {
    flex: 1 1 0;
    min-width: 0;
    padding-right: 0.25rem;
    padding-left: 0.25rem;
  }

}

.export-description {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem;
  background-color: rgba($primary, 0.05);
  border-radius: 6px;
  border-left: 5px solid $primary;
  color: $body-color;
}

.dark-layout {
  .export-section {
    border-color: $theme-dark-border-color;
    background-color: $theme-dark-card-bg;

    &:hover {
      box-shadow: $theme-dark-box-shadow !important;
    }
  }

  .export-header {
    background: linear-gradient(135deg, $theme-dark-body-bg 0%, darken($theme-dark-body-bg, 3%) 100%);
    border-bottom-color: $theme-dark-border-color;
  }

  .export-body {
    background-color: $theme-dark-card-bg;
  }

  .custom-select {
    background-color: $theme-dark-input-bg;
    border-color: $theme-dark-input-border-color;
    color: $theme-dark-body-color;

    &:focus {
      border-color: $primary;
      background-color: $theme-dark-input-bg;
    }
  }

  .export-description {
    background-color: rgba($primary, 0.1);
    color: $theme-dark-body-color;
  }
}
</style>
